@let isSm = viewportSelectors.isSm | select;

@let hasData = paginationSelectors.hasData | select;
@let awaitingFirstLoad = defaultSelectorContainer.awaitingFirstLoad | select;
@let awaitingAnyLoad = defaultSelectorContainer.awaitingAnyLoad | select:false;

@let rowCount = rowCountSelector | select:0;
@let hideHeaderRow = alwaysHideHeaderRow || (responsiveHideHeaderRow && isSm);
@let showVerticalActions = verticalActionsSm && isSm;

<!--
  While awaiting load, we must choose to display the columns for either the "has data" or "has no data" state.  It makes sense to choose the former because
  loading often results from changing pages for a paginated table, so we want to maintain the columns during the page change.  For initial loads, we'll
  display the "has data" state momentarily and then switch to the "has no data" state when loading is complete and we've confirmed we have no data.
-->
@let columnOrder = (awaitingAnyLoad || hasData ? columnOrderSelector : columnOrderForNoDataSelector) | select:[];
@let columnSpanForFullWidthCells = columnOrder.length;

<mx-table-height-adjuster>
  <table mat-table
         [dataSource]="dataSource"
         matSort (matSortChange)="onMatSortChange($event)"
         [attr.aria-label]="tableName"
         [multiTemplateDataRows]="useRowBasedExpansion"
         [ngClass]="{ 'mx-table-no-pagination': !showPagination, 'mx-table-no-bottom-border': !showBottomBorder, 'mx-table-no-header': hideHeaderRow }">
    <tr mat-header-row *matHeaderRowDef="columnOrder" [class.h-0]="hideHeaderRow"></tr>

    <tr mat-row *matRowDef="let row; columns: columnOrder" [ngClass]="generateRowCssClassesFor(row)"></tr>

    @if (useHiddenDataIndicatorRow) {
      <tr mat-footer-row *matFooterRowDef="hiddenDataIndicatorColumnOrder | select:[]" mxHiddenUntilLoaded [reveal]="!awaitingFirstLoad"
          [class.h-0]="!(showHiddenDataIndicatorRow | select:false)">
    }

    @if (useRowBasedExpansion) {
      <tr mat-row class="mx-expandable-row-row-based-companion" *matRowDef="let row; columns: ['expanded']; when: renderRowBasedExpansionCompanion"
          [ngClass]="{ 'mx-expandable-row-row-based-companion-expanded': rowExpansionService.isExpanded(row) }">
      </tr>
    }

    <ng-container matColumnDef="expanded">
      <td *matCellDef="let row" mat-cell [attr.colspan]="columnSpanForFullWidthCells">
        <div class="mx-expansion-content" [@expansionRowContentTransition]="rowExpansionService.isExpanded(row) ? 'expanded' : 'collapsed'">
          <ng-template #rowComponentContainer mxDynamicComponentHost [data]="{ row: row }"/>
        </div>
      </td>
    </ng-container>

    @if (useStandardNoDataMessageRow) {
      <!--
        We have to use mat-* CSS classes with *matNoDataRow instead of the usual mat-* directives we use everywhere else, because... reasons?
        See https://stackoverflow.com/questions/62698551/angular-material-matnodatarow-doesnt-work#comment112097235_62706478
      -->
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell border-b-0" [colSpan]="columnSpanForFullWidthCells">
          <p mxHiddenUntilLoaded [reveal]="!awaitingFirstLoad">
            {{ noDataRowMessage | select }}
          </p>
        </td>
      </tr>
    }

    <ng-container matColumnDef="_expansionRowToggle">
      <th mat-header-cell *matHeaderCellDef scope="col" class="mx-header-cell-for-icon-button"></th>

      <td mat-cell *matCellDef="let row">
        <div class="mx-cell-content">
          @if (useColumnBasedExpansionFor(row) || useRowBasedExpansionFor(row)) {
            <mx-expansion-toggle [rowObject]="row"/>
          }
        </div>
      </td>
    </ng-container>

    <ng-container matColumnDef="_rowSelectionCheckboxes">
      <th mat-header-cell *matHeaderCellDef scope="col" class="mx-header-cell-for-icon-button">
        @if (!hideHeaderRow) {
          <div class="mx-cell-content">
            <mx-all-rows-selection-checkbox [data]="rowSelectionDataSourceSelector | select:[]" [rowSelectionChangeAction]="rowSelectionChangeAction"/>
          </div>
        }
      </th>

      <td mat-cell *matCellDef="let row">
        <div class="mx-cell-content">
          <mx-row-selection-checkbox [rowObject]="row" [enabled]="rowSelectionEnabledFor(row)" [tooltips]="rowSelectionTooltipsFor(row)"/>
        </div>
      </td>
    </ng-container>

    @for (column of columns; track column.columnName; let columnIndex = $index) {
      <ng-container [matColumnDef]="column.columnName">
        <th mat-header-cell *matHeaderCellDef [mat-sort-header]="column.sortKey" [disabled]="!column.sortable || !rowCount"
            class="{{ column.headerCssClasses }}">
          @if (!hideHeaderRow) {
            <div class="mx-cell-content" [matTooltip]="column.headerToolTip">
              {{ column.header }}
            </div>
          }
        </th>

        <td mat-cell *matCellDef="let row">
          <div class="mx-cell-content" [ngClass]="generateCellCssClassesFor(row, column.cssClasses)">
            @if (column.isComponent) {
              <ng-template #cellComponentContainer mxDynamicComponentHost [data]="{ row: row, column: column }"/>
            } @else {
              <!-- not a component -->
              @if (column.isChip) {
                <mat-chip-set>
                  @if (column.valueSelectorCreator) {
                    @if (column.isStatusChip) {
                      <mx-status-chip [status]="column.valueSelectorCreator(row) | select" [colors]="column.statusChipColors"
                                      [displayableStatusNames]="column.displayableStatusNames"/>
                    } @else {
                      <mat-chip [color]="column.chipColorFor(row)">
                        {{ column.valueSelectorCreator(row) | select | format:column.format }}
                      </mat-chip>
                    }
                  }
                </mat-chip-set>
              } @else {
                <!-- not a chip -->
                @if (column.isLink) {
                  @if (column.isSimpleLink) {
                    <a (click)="column.clickHandler(row)">
                      <ng-container *ngTemplateOutlet="cellDataOnly"/>
                    </a>
                  } @else {
                    <!-- not a simple link -->
                    <a [mxNamedRouterLink]="column.routeName" [mxNamedRouterLinkParams]="buildLinkParamsFor(column, row)">
                      <ng-container *ngTemplateOutlet="cellDataOnly"/>
                    </a>
                  }
                } @else {
                  <!-- not a link -->
                  <ng-container *ngTemplateOutlet="cellDataOnly"/>
                }
              }
            }

            <ng-template #cellDataOnly>
              @if (column.valueSelectorCreator) {
                @let cellValue = column.valueSelectorCreator(row) | select | format:column.format;

                @if (column.truncate) {
                  <span mxTruncate>{{ cellValue }}</span>
                } @else {
                  <span>{{ cellValue }}</span>
                }
              }
            </ng-template>
          </div>

          @if (useColumnBasedExpansionFor(row)) {
            <div class="mx-expansion-content" [@expansionRowContentTransition]="rowExpansionService.isExpanded(row) ? 'expanded' : 'collapsed'">
              @for (expansionCellDefinition of expansionCellDefinitionsFor(columnIndex); track $index) {
                <div class="flex flex-col text-body-small">
                  @if (expansionCellDefinition.showIf(row)) {
                    <header mxTruncate class="min-h-[1lh] {{expansionCellDefinition.headerCssClasses}}">
                      {{ expansionCellDefinition.headerFor(row) }}
                    </header>

                    <!--
                       If you're seeing IntelliJ incorrectly reporting unresolved properties here (red underlines), it's because it fails to narrow the
                       discriminated union across @if/@else (this code worked with the old *ngIf). Tried various refactorings but nothing helped.
                       Angular compiles this correctly.

                       See https://youtrack.jetbrains.com/projects/WEB/issues/WEB-72936/False-positive-Type-mismatch-lint-in-angular-HTML-template.

                       And if you're NOT seeing errors, and you’ve confirmed the IDE now narrows this correctly, please delete this comment!  Apparently they
                       finally fixed this issue.  (But don't be fooled -- these errors seem to appear/disappear almost randomly with changes to the file.)
                    -->
                    @if (expansionCellDefinition.hasTextValue) {
                      <span mxTruncate class="min-h-[1lh]">
                        {{ expansionCellDefinition.valueFor(row) | format:expansionCellDefinition.format }}
                      </span>
                    } @else {
                      <ng-template #expansionCellComponentContainer mxDynamicComponentHost
                                   [data]="{
                                             row: row,
                                             componentClass: expansionCellDefinition.componentClass,
                                             componentProperties: expansionCellDefinition.componentProperties
                                           }"/>
                    }
                  }
                </div>
              }
            </div>
          }
        </td>

        <td mat-footer-cell *matFooterCellDef [colSpan]="columnSpanForFullWidthCells">
          <div class="flex flex-row justify-center items-center gap-x-4">
            <span>{{ hiddenDataIndicatorRowMessage | select }}</span>

            @if (showHiddenDataIndicatorActionButton | select) {
              <mx-text-button [label]="hiddenDataIndicatorRowButtonLabel | select" (click)="hiddenDataIndicatorRowClickHandler()"/>
            }
          </div>
        </td>
      </ng-container>
    }

    <ng-container matColumnDef="_actions">
      <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -- This is a dynamic style that is calculated programmatically. -->
      <th mat-header-cell *matHeaderCellDef [style.width.rem]="actionsColumnWidthInRemsSelector | select:0" [class.max-w-10]="showVerticalActions">
        @if (showActionsHeaderText && !hideHeaderRow) {
          @let actionsHeaderText = actionsColumnHeaderTextSelector | select:'';

          <div class="mx-cell-content">
            {{ actionsHeaderText }}
          </div>
        }
      </th>

      <td mat-cell *matCellDef="let row; let rowIndex = index" [class.max-w-10]="showVerticalActions">
        <div class="mx-cell-content flex justify-end" [ngClass]="{ 'flex-row': !showVerticalActions, 'max-w-10 flex-col gap-y-4': showVerticalActions }">
          @if (showActionButtons) {
            @for (actionButton of actionButtons; track $index) {
              @if (actionButton.visibleFor(row) | select:false) {
                @let color = actionButton.colorFor(row) | select:'on-surface-variant';
                @let enabled = actionButton.enabledFor(row) | select:false;
                @let tooltip = actionButton.tooltipFor(row) | select:'';
                @let uniqueClass = `mx-pf-r-${rowIndex}-action-${$index}`;

                @if (showVerticalActions) {
                  <mx-outlined-button [customContentTemplate]="buttonIcon" [renderAsIconOnly]="true" [color]="color" [enabled]="enabled" [tooltip]="tooltip"
                                      (click)="actionButton.clickHandler(row)" [class]="uniqueClass"/>
                } @else {
                  <mx-icon-button [customContentTemplate]="buttonIconWrapper" [color]="color" [enabled]="enabled" [tooltip]="tooltip"
                                  (click)="actionButton.clickHandler(row)" [class]="uniqueClass"/>
                }

                <ng-template #buttonIconWrapper>
                  @if ((actionButton.badgeFor(row) | select:''); as badge) {
                    <mat-icon [ngClass]="generateCellCssClassesFor(row, actionButton.cssClasses)" [matBadge]="badge" matBadgeSize="small"
                              class="mat-badge-error">
                      {{ actionButton.iconName }}
                    </mat-icon>
                  } @else {
                    @if ((actionButton.superscriptFor(row) | select:''); as superscript) {
                      <span [matBadge]="superscript" class="mat-badge-transparent">
                        <ng-container *ngTemplateOutlet="buttonIcon"/>
                      </span>
                    } @else {
                      <ng-container *ngTemplateOutlet="buttonIcon"/>
                    }
                  }
                </ng-template>

                <ng-template #buttonIcon>
                  <mat-icon [ngClass]="generateCellCssClassesFor(row, actionButton.cssClasses)">{{ actionButton.iconName }}</mat-icon>
                </ng-template>
              }
            }
          }

          @if (showKebabMenuActions) {
            @if (visibleKebabMenuActionsFor(row); as visibleKebabMenuActions) {
              <mx-icon-button [matMenuTriggerFor]="rowActionsMenu" icon="more_vert" color="on-surface-variant"/>

              <mat-menu #rowActionsMenu="matMenu">
                <ng-template matMenuContent>
                  @for (kebabMenuAction of visibleKebabMenuActions; track kebabMenuAction.label) {
                    <button mat-menu-item (click)="kebabMenuAction.clickHandler(row)">
                      <span>{{ kebabMenuAction.label }}</span>
                    </button>
                  }
                </ng-template>
              </mat-menu>
            } @else {
              <!-- No button.  This span takes up space like a kebab button. -->
              <span class="size-10 shrink-0"></span>
            }
          }
        </div>
      </td>
    </ng-container>
  </table>
</mx-table-height-adjuster>

<!-- Note that the "loaded" class is removed inside PaginationComponent when the user clicks the previous/next page buttons, and eventually restored here. -->
@if (showPagination && hasData) {
  <mx-pagination [class.loaded]="!awaitingAnyLoad" [updateAction]="paginationAction"/>
}

@if (useCustomNoDataComponent) {
  <div [hidden]="awaitingFirstLoad || hasData">
    <ng-template #customNoDataComponentContainer mxDynamicComponentHost/>
  </div>
}
